import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@goodday-clone/ui'
import { AlertTriangle, ArrowLeft, CalendarClock } from 'lucide-react'
import { LeadDetailModal, LeadDetailData } from '@/components/sales/lead-detail-modal'
import { LeadsSubNav } from '@/features/leads/LeadsSubNav'
import { useOverdueFollowups } from '@/features/leads/useOverdueFollowups'
import {
  daysOverdue,
  formatFollowupDue,
  formatLeadValue,
  overdueLabel,
  priorityBadgeClass,
  type OverdueRow,
} from '@/features/leads/overdueFollowups'

export default function LeadsOverduePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: rows = [], isLoading } = useOverdueFollowups()
  const [selectedLead, setSelectedLead] = useState<LeadDetailData | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const stats = useMemo(() => {
    const leads = new Set(rows.map((r) => r.lead._id))
    const longest = rows.reduce((max, r) => Math.max(max, daysOverdue(r.followup.dueAt)), 0)
    return {
      followups: rows.length,
      leads: leads.size,
      highPriority: rows.filter((r) => r.followup.priority === 'High').length,
      longest,
    }
  }, [rows])

  const handleRowClick = (row: OverdueRow) => {
    const lead = row.lead
    setSelectedLead({
      id: lead._id,
      name: lead.name,
      company: lead.company || '—',
      contactPerson: lead.name,
      email: lead.email || '',
      phone: lead.phone || '',
      assignee: lead.assignee || 'Unassigned',
      industry: lead.industry,
      source: lead.source || 'Website',
      status: lead.status || 'New',
      value: formatLeadValue(lead.value),
      interest: lead.interest || 'Warm',
    })
    setIsDetailOpen(true)
  }

  // A follow-up completed inside the modal has to leave this list, and the
  // modal owns its own follow-up cache, so the refresh is triggered on close.
  const handleCloseDetail = () => {
    setIsDetailOpen(false)
    queryClient.invalidateQueries({ queryKey: ['leads', 'overdue-followups'] })
  }

  return (
    <div className="space-y-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Overdue Follow-ups</h1>
            <p className="text-sm text-muted-foreground">Pending follow-ups whose due date has already passed</p>
          </div>
        </div>
        {stats.followups > 0 && (
          <Badge className="gap-1.5 border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            {stats.followups} overdue
          </Badge>
        )}
      </div>

      <LeadsSubNav current="overdue" />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Overdue follow-ups', value: String(stats.followups), tone: 'text-red-600 dark:text-red-400' },
          { label: 'Leads affected', value: String(stats.leads), tone: 'text-amber-600 dark:text-amber-400' },
          { label: 'High priority', value: String(stats.highPriority), tone: 'text-orange-600 dark:text-orange-400' },
          { label: 'Longest overdue', value: `${stats.longest} days`, tone: 'text-foreground' },
        ].map((tile) => (
          <Card key={tile.label}>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{tile.label}</p>
              <p className={`mt-1 text-2xl font-bold ${tile.tone}`}>{tile.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="py-4 px-6 border-b border-border/50 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">Overdue Follow-ups ({rows.length})</CardTitle>
          <Badge variant="outline" className="text-xs">Oldest first</Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">
                <tr>
                  <th className="px-6 py-3">Lead Name</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Follow-up Activity</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Assigned Salesperson</th>
                  <th className="px-4 py-3">Due Date &amp; Time</th>
                  <th className="px-6 py-3 text-right">Lead Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">Loading overdue follow-ups...</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <CalendarClock className="h-6 w-6 text-emerald-500/70" />
                        <p className="font-medium text-sm text-foreground">Nothing is overdue</p>
                        <p className="text-xs text-muted-foreground">
                          Every pending follow-up is still within its due date.
                        </p>
                        <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => navigate('/app/leads')}>
                          Back to All Leads
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.followup.id}
                      onClick={() => handleRowClick(row)}
                      className="hover:bg-muted/40 cursor-pointer transition-colors group"
                      data-testid="overdue-followup-row"
                    >
                      <td className="px-6 py-3.5">
                        <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {row.lead.name}
                        </p>
                        <p className="text-xs text-muted-foreground">{row.lead.email}</p>
                      </td>
                      <td className="px-4 py-3.5 font-medium text-foreground">{row.lead.company || '—'}</td>
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-foreground">{row.followup.title}</p>
                        <p className="text-xs text-muted-foreground">{row.followup.type}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full font-semibold ${priorityBadgeClass(row.followup.priority)}`}>
                          {row.followup.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground">
                        {row.followup.rep || row.lead.assignee || '—'}
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="text-xs text-muted-foreground">{formatFollowupDue(row.followup.dueAt)}</p>
                        <p className="text-xs font-semibold text-red-600 dark:text-red-400">
                          {overdueLabel(row.followup.dueAt)}
                        </p>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <Badge variant="secondary" className="text-xs font-normal">{row.lead.status || 'New'}</Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <LeadDetailModal
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
        lead={selectedLead}
        currentSectionName="Overdue Follow-ups"
        currentSectionHref="/app/leads/overdue"
      />
    </div>
  )
}
